import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { Checkbox } from '@els/els-react--checkbox';
import { TogglePanel } from '@els/els-react--toggle-panel';
import { commaFormatNumber } from '@Utils/String';
import { stripExceptionTags } from '@Services/HtmlSanitizeService';

function ActiveChunkOptimizedRenderingRow({ data, index, style }) {
  const { activeChunk, setSize, sizeMap, onCheckboxClick } = data;

  const rowRef = useRef();

  // Set size on mount on resize observer
  useEffect(() => {
    let observer;

    if (rowRef.current && sizeMap.current) {
      observer = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const { height } = rowRef.current.getBoundingClientRect();
          if (Math.round(height) !== Math.round(sizeMap.current[index])) {
            setSize(index, height);
          }
        });
      });

      observer.observe(rowRef.current);
    }

    return () => observer && observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set size on mount
  useEffect(() => {
    const { height } = rowRef.current.getBoundingClientRect();
    setSize(index, height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const item = activeChunk.get(index);
  const title = stripExceptionTags(item.get('title'));
  const id = item.get('id');
  const size = item.get('size');
  const selected = item.get('selected');
  const affiliation = item.get('affiliation');
  const synonyms = item.get('synonyms');

  const onCheckboxChange = useCallback(() => {
    onCheckboxClick(id, selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <div className="new-author-checkboxes-dialog__active-chunk-bordered" style={style}>
      <div className="new-author-checkboxes-dialog__active-chunk-row" ref={rowRef}>
        <Checkbox
          data-e2e={`author-chunk-checkbox-${id}`}
          className="new-author-checkboxes-dialog__active-chunk-checkbox"
          checked={selected}
          shouldHideLabel
          onChange={onCheckboxChange}
        />
        <div className="new-author-checkboxes-dialog__active-chunk-title">
          <TogglePanel data-e2e={`author-chunk-toggle-${id}`} id={id} header={title} size="s" headerLevel="4">
            {synonyms &&
              synonyms.toArray().map((synonym) => (
                <div key={synonym} className="new-author-checkboxes-dialog__active-chunk-synonym">
                  {synonym}
                </div>
              ))}
          </TogglePanel>
          {affiliation && <div className="new-author-checkboxes-dialog__active-chunk-affiliation">{affiliation}</div>}
        </div>
        <span className="new-author-checkboxes-dialog__active-chunk-size">{commaFormatNumber(size)}</span>
      </div>
    </div>
  );
}

ActiveChunkOptimizedRenderingRow.propTypes = {
  data: PropTypes.shape({
    activeChunk: PropTypes.instanceOf(List),
    setSize: PropTypes.func.isRequired,
    sizeMap: PropTypes.shape({
      current: PropTypes.object,
    }),
    onCheckboxClick: PropTypes.func.isRequired,
  }),
  index: PropTypes.number,
  style: PropTypes.object,
};

function ActiveChunk({ activeChunk, onCheckboxClick }) {
  const listRef = useRef();
  const sizeMap = useRef({});

  const setSize = useCallback((index, size) => {
    if (!listRef.current) return;
    sizeMap.current = { ...sizeMap.current, [index]: size };
    listRef.current.resetAfterIndex(index);
  }, []);

  const getSize = useCallback((index) => sizeMap.current[index] || 38, []);

  return (
    <div className="new-author-checkboxes-dialog__active-chunk-wrapper">
      <AutoSizer>
        {({ height, width }) => (
          <VariableSizeList
            ref={listRef}
            className="new-author-checkboxes-dialog__active-chunk"
            height={height}
            width={width}
            itemCount={activeChunk.count()}
            itemSize={getSize}
            overscanCount={5}
            itemData={{ activeChunk, setSize, sizeMap, onCheckboxClick }}>
            {ActiveChunkOptimizedRenderingRow}
          </VariableSizeList>
        )}
      </AutoSizer>
    </div>
  );
}

ActiveChunk.propTypes = {
  activeChunk: PropTypes.instanceOf(List),
  onCheckboxClick: PropTypes.func.isRequired,
};

export default ActiveChunk;
